import { Component, OnDestroy, ChangeDetectionStrategy, Inject } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { ProjectService } from 'app/services/project.service';
import { Subject, takeUntil, BehaviorSubject, map, shareReplay, tap, filter, Observable, startWith, debounceTime, combineLatest } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ProjectStatus } from 'app/models/project/project';
import { HttpErrorResponse } from '@angular/common/http';
import { NewProjectComponent } from 'app/modules/organization/modules/projects/components/new-project/new-project.component';
import { UsersService } from 'app/services/users.service';
import { UserService } from 'app/services/user.service';

@Component({
  selector: 'tm-add-task',
  templateUrl: './add-task.component.html',
  styleUrls: ['./add-task.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddTaskComponent implements OnDestroy {
  readonly error$ = new Subject<string | void>();
  readonly projects$ = this.projectService.projects$.pipe(
    map(projects => projects.filter(pr => pr.status === ProjectStatus.Normal)),
    tap(projects => {
      if (projects.length === 1) {
        this.taskForm.get('project')?.setValue(projects[0].id);
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  private readonly destroy$ = new Subject<void>();
  submitting$ = new BehaviorSubject<boolean>(false);
  readonly taskForm: FormGroup = this.fb.group({
    project: ['', [Validators.required]],
    taskName: ['', [Validators.required]],
    taskDescription: null,
    assigneeId: null,
  });
  readonly membersFilterCtrl = new FormControl<string>('');
  readonly usersFilter$ = combineLatest({
    members: this.usersService.get(), searchText: this.membersFilterCtrl.valueChanges.pipe(startWith(''), debounceTime(150))
  }).pipe(
    map(({ searchText, members }) => {
      if (searchText) {
        return members.filter(country => country.displayName?.toLowerCase().indexOf((searchText || '').toLowerCase()) > -1);
      } else return members
    })
  );

  get isOrganizationOwner$() {
    return this.userService.isOrganizationOwner$;
  }

  constructor(
    private readonly userService: UserService,
    private readonly usersService: UsersService,
    public dialog: MatDialog,
    private readonly dialogRef: MatDialogRef<AddTaskComponent>,
    @Inject(MAT_DIALOG_DATA) private readonly projectId: string,
    private readonly fb: FormBuilder,
    private readonly projectService: ProjectService) {
    this.taskForm.get('project').setValue(projectId);
  }

  get formInValid(): boolean {
    return this.taskForm.invalid;
  }

  createProject() {
    (this.dialog.open(NewProjectComponent).addPanelClass(['min-w-80', 'max-w-full', 'w-full', 'h-full', 'sm:h-fit', 'sm:w-120', 'md:w-180']).afterClosed() as Observable<string | undefined>).pipe(
      takeUntil(this.destroy$),
      filter((id) => id !== undefined),
    ).subscribe((id) => {
      this.projectService.refresh();
      this.taskForm.patchValue({
        project: id
      })
    });
  }

  submit(): void {
    this.error$.next();
    const projectId = this.taskForm.get('project').value;
    this.submitting$.next(true);
    const data = {
      title: this.taskForm.get('taskName').value,
      description: this.taskForm.get('taskDescription').value,
    }
    this.projectService.createTicket(projectId, data).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => this.dialogRef.close('success'),
      error: (err: HttpErrorResponse) => {
        this.submitting$.next(false);
        this.error$.next(err?.message || 'Error!');
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.submitting$.complete();
  }
}
